import { ImageName } from "@mcleod/images";
import {
    ClickEvent, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, Image, LookupModelSearchEvent, Switch, Tab,
    TableContentsChangedEvent, TableRow, TableRowDisplayEvent, TableRowMode, Textbox
} from "@mcleod/components";
import { AutogenLayoutBillingFreightItemTable } from "./autogen/AutogenLayoutBillingFreightItemTable";
import { BrltlUtil } from "@mcleod/dispatch";
import { ModelRow, NumberUtil, StringUtil } from "@mcleod/core";
import { RowBrltlOrderFreightGroupItem } from "@mcleod/dispatch/src/models/ModelBrltlOrderFreightGroupItem";
import { CommonTooltips } from "@mcleod/common";

interface FgiData {
    row: TableRow,
    textLength: Textbox,
    textWidth: Textbox,
    textHeight: Textbox,
    textHandlingUnits: Textbox,
    textWeight: Textbox,
    textboxLinearUnits: Textbox,
    textboxCubicUnits: Textbox
}

export class BillingFreightItemTable extends AutogenLayoutBillingFreightItemTable {
    public doBeforeCommoditySearch: (filter: any) => void;

    sourceFreightGroupItemAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.SEARCH) {
            this.setFgiTotals();
        }
    }

    tableFreightItemsOnContentsChanged(event: TableContentsChangedEvent) {
        this.setFgiTotals();
    }

    tableFreightItemsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.target as TableRow;
        const defaultDeleteRow = tableRow.deleteRow.bind(tableRow);
        tableRow.deleteRow = async () => {
            if (tableRow.mode === TableRowMode.ADD) {
                const dataSource = this.tableFreightItems.dataSource;
                const dataIndex = dataSource?.findRowInData(tableRow.data as ModelRow, true) ?? -1;
                if (dataIndex >= 0)
                    dataSource.data.splice(dataIndex, 1);
                this.tableFreightItems.removeRow(tableRow.index);
                return;
            }
            await defaultDeleteRow();
        };
        const commodityTextbox = tableRow.findComponentById("textCommodity") as Textbox;
        commodityTextbox.quickInfoLayout = "lme/dispatch/CommodityQuickInfo";
        const freightClassTextbox = tableRow.findComponentById("textFreightClass") as Textbox;
        const nmfcClassCode = tableRow.data.get("nmfc_class_code");
                if (!StringUtil.isEmptyString(nmfcClassCode)) {
                    CommonTooltips.setTooltipContentLayout(freightClassTextbox, null, "lme/dispatch/ScreenCodesQuickInfo", null,
                        { field_code: nmfcClassCode, field_name: "nmfc_class_code" });
                }
        const modelRow = tableRow.data;
        const isHazmat = modelRow.get("hazmat") === "Y";
        this.configureForHazmat(tableRow, isHazmat, false);
    }

    configureForHazmat(row: TableRow, isHazmat: boolean, expandRow: boolean) {
        const commodityTextBox = row.findComponentById("textCommodity") as Textbox;
        let hazmatImage = null;
        if (isHazmat)
            hazmatImage = new Image({ name: ImageName.triangleWarning, color: "error", marginLeft: 8 });
        commodityTextBox.imagePre = hazmatImage;
        const hazmatDetails = row.findComponentById("tabHazmatDetails") as Tab;
        hazmatDetails.expandCollapseTab(isHazmat, true);
        hazmatDetails.findComponentById("panelHazmat").displayData(row.data, null, 0); // workaround for displaying data in a tab on load
    }


    setFgiTotals() {
        const totalWeight = BrltlUtil.getFgiTotal(this.tableFreightItems.dataSource, "weight");
        const totalHu = BrltlUtil.getFgiTotal(this.tableFreightItems.dataSource, "handling_units");
        this.setTotalCaptions(totalWeight, totalHu);
    }

    setTotalCaptions(weight: number, handlingUnits: number) {
        this.labelTotalWeight.text = NumberUtil.formatDecimal(weight ?? 0, "#,###.#") + " lbs";
        this.labelTotalUnits.text = (handlingUnits ?? 0).toString();
    }

    sourceBrltlOrderFreightGroupItemBeforeExecution(event: DataSourceExecutionEvent) {
        this.removeBlankFreightItem();
    }

    removeBlankFreightItem() {
        if (this.tableFreightItems.rows.length == 1) {
            const tableRow = this.tableFreightItems.rows[0];
            const modelRow = tableRow.data as RowBrltlOrderFreightGroupItem;
            if (modelRow.isNull("commodity_id") && modelRow.isNull("nmfc_class_code") && modelRow.isNull("req_spots") && modelRow.isNull("weight")) {
                tableRow.deleteRow();
            }
        }
    }

    textFreightClassBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.filtered_record = false;
    }

    textPackagingTypeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.field_table_alias = "FGI";
        event.filter.field_name = "fgi_packaging_type_code";
    }

    textCommodityBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        if (this.doBeforeCommoditySearch)
            this.doBeforeCommoditySearch(event.filter);
    }

    setFreightGroupItems(fgis: any[]) {
        const data = fgis[0]?.data["freight_group_items"];
        if(data) {
            let handlingUnits = 0;
            let weight = 0.0;

            const rows = [];
            data.forEach(fgi => {
                const mappedData = new ModelRow("lme/ar/billing-freight-group", true, fgi);
                const hu = mappedData.get("handling_units");
                handlingUnits += hu;
                const w = mappedData.get("weight");
                weight += w;
                rows.push(mappedData);
            });
            this.sourceBillingFreightGroup.setRowsAndMode(DataSourceMode.NONE, rows);
            this.labelTotalUnits.text = NumberUtil.formatInteger(handlingUnits);
            this.labelTotalWeight.text = NumberUtil.formatDecimal(weight, "#,###.#") + " lbs";
        }
    }

}
